/**
 * AssignRouteDialog — Assign/reassign/unassign a SF route to a staff member
 */

import { useState, useMemo } from 'react';
import { format, parseISO } from 'date-fns';
import { nl } from 'date-fns/locale';
import type { RouteListItem } from '../../lib/salesforce/types';
import type { Staff, LeaveEntry } from '../../stores/workforce-store';
import type { ScheduleEntry, AvailabilityEntry } from '../../stores/schedule-store';

interface AssignRouteDialogProps {
  route: RouteListItem;
  staffList: Staff[];
  schedule: ScheduleEntry[];
  availability: AvailabilityEntry[];
  leaveEntries?: LeaveEntry[];
  currentStaffId?: string | null;
  onAssign: (staffId: string) => void;
  onUnassign?: () => void;
  /** Opent de "verplaats naar andere dag"-modal (sluit deze dialog) */
  onReschedule?: () => void;
  onClose: () => void;
  isAssigning: boolean;
  error?: string | null;
}

type StaffSuitability = 'ideal' | 'partial' | 'available_busy' | 'other_available' | 'unknown' | 'unavailable';

function getStaffSuitability(
  staff: Staff,
  availability: AvailabilityEntry[],
  schedule: ScheduleEntry[],
  leaveEntries: LeaveEntry[],
  date: string,
): StaffSuitability {
  const avail = availability.find(a => a.staff_id === staff.id && a.date === date);
  const shifts = schedule.filter(s => s.staff_id === staff.id && s.date === date);
  const hasLeave = leaveEntries.some(l => l.staff_id === staff.id && l.date === date);

  if (hasLeave) return 'unavailable';
  if (avail && !avail.is_available) return 'unavailable';

  const isRouteTeam = staff.team === 'route';
  const isAvailable = avail?.is_available ?? null; // null = unknown
  const hasShifts = shifts.length > 0;

  if (isAvailable === false) return 'unavailable';
  // Wie maar een dagdeel kan is niet "ideaal": routes starten in de ochtend, dus
  // een avondvenster past er meestal niet op. Apart rangschikken en het venster
  // tonen, zodat de planner de keuze zelf maakt in plaats van misleid te worden.
  if (isAvailable && (avail?.time_from || avail?.time_to)) return 'partial';
  if (isAvailable && isRouteTeam && !hasShifts) return 'ideal';
  if (isAvailable && isRouteTeam && hasShifts) return 'available_busy';
  if (isAvailable && !isRouteTeam) return 'other_available';
  // Unknown availability
  if (isRouteTeam && !hasShifts) return 'unknown';
  return 'unknown';
}

const suitabilityOrder: Record<StaffSuitability, number> = {
  ideal: 0,
  partial: 1,
  available_busy: 2,
  other_available: 3,
  unknown: 4,
  unavailable: 5,
};

const suitabilityIndicator: Record<StaffSuitability, { dot: string; label: string }> = {
  ideal: { dot: 'bg-emerald-400', label: 'beschikbaar' },
  partial: { dot: 'bg-emerald-200', label: 'alleen een dagdeel' },
  available_busy: { dot: 'bg-yellow-400', label: 'beschikbaar, al ingepland' },
  other_available: { dot: 'bg-emerald-400', label: 'beschikbaar' },
  unknown: { dot: 'bg-slate-300', label: 'onbekend' },
  unavailable: { dot: 'bg-red-400', label: 'niet beschikbaar' },
};

const teamLabels: Record<string, string> = {
  wasserij: 'wasserij',
  route: 'route',
  kantoor: 'kantoor',
  algemeen: 'algemeen',
};

export function AssignRouteDialog({
  route,
  staffList,
  schedule,
  availability,
  leaveEntries = [],
  currentStaffId,
  onAssign,
  onUnassign,
  onReschedule,
  onClose,
  isAssigning,
  error,
}: AssignRouteDialogProps) {
  const [selectedStaffId, setSelectedStaffId] = useState<string | null>(currentStaffId || null);
  const isEditMode = !!currentStaffId;

  const dateLabel = format(parseISO(route.datum), 'EEEE d MMMM', { locale: nl });

  const sortedStaff = useMemo(() => {
    const activeStaff = staffList.filter(s => s.is_active);
    return activeStaff
      .map(staff => ({
        staff,
        suitability: getStaffSuitability(staff, availability, schedule, leaveEntries, route.datum),
        shifts: schedule.filter(s => s.staff_id === staff.id && s.date === route.datum),
      }))
      .sort((a, b) => {
        const orderDiff = suitabilityOrder[a.suitability] - suitabilityOrder[b.suitability];
        if (orderDiff !== 0) return orderDiff;
        return a.staff.name.localeCompare(b.staff.name);
      });
  }, [staffList, availability, schedule, leaveEntries, route.datum]);

  const handleAssign = () => {
    if (selectedStaffId) onAssign(selectedStaffId);
  };

  return (
    <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50" onClick={onClose}>
      <div className="bg-white rounded-2xl p-6 max-w-md w-full mx-4 shadow-xl max-h-[80vh] flex flex-col" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <h3 className="text-lg font-semibold text-main mb-1">
          {route.routeName || route.name}
        </h3>
        <p className="text-sm text-muted mb-1">{dateLabel}</p>
        <p className="text-xs text-muted mb-4">
          {route.stopCountOphalen > 0 && `${route.stopCountOphalen} ophalen`}
          {route.stopCountOphalen > 0 && route.stopCountAfleveren > 0 && ' \u00b7 '}
          {route.stopCountAfleveren > 0 && `${route.stopCountAfleveren} afleveren`}
          {route.locked && ' \u00b7 vergrendeld'}
        </p>

        {route.locked && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mb-4 text-xs text-amber-700">
            Deze route is vergrendeld. Toewijzing is niet mogelijk.
          </div>
        )}

        {/* Staff list */}
        <div className="flex-1 overflow-y-auto -mx-2 px-2 mb-4 min-h-0">
          <p className="text-xs font-medium text-muted mb-2">Toewijzen aan:</p>
          <div className="space-y-1">
            {sortedStaff.map(({ staff, suitability, shifts }) => {
              const indicator = suitabilityIndicator[suitability];
              const isUnavailable = suitability === 'unavailable';
              const isDisabled = isUnavailable || route.locked;
              const isSelected = selectedStaffId === staff.id;
              const isCurrent = currentStaffId === staff.id;

              return (
                <button
                  key={staff.id}
                  onClick={() => !isDisabled && setSelectedStaffId(staff.id)}
                  disabled={isDisabled}
                  className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-left transition-colors ${
                    isSelected
                      ? 'bg-indigo-50 border border-indigo-300'
                      : isDisabled
                        ? 'opacity-40 cursor-not-allowed'
                        : 'hover:bg-slate-50 border border-transparent'
                  }`}
                >
                  {/* Suitability dot */}
                  <span className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${indicator.dot}`} />

                  {/* Staff info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-main truncate">
                        {staff.name.split(' ')[0]}
                      </span>
                      {isCurrent && (
                        <span className="text-[10px] px-1.5 py-0.5 bg-indigo-100 text-indigo-600 rounded font-medium">
                          huidig
                        </span>
                      )}
                    </div>
                    <div className="text-[10px] text-muted flex items-center gap-1.5">
                      <span>{teamLabels[staff.team || ''] || 'algemeen'}</span>
                      <span className="text-slate-300">&middot;</span>
                      <span>{leaveEntries.some(l => l.staff_id === staff.id && l.date === route.datum) ? 'verlof' : indicator.label}</span>
                      {shifts.length > 0 && (
                        <>
                          <span className="text-slate-300">&middot;</span>
                          <span>
                            {shifts.map(s => `${s.shift_type || 'dienst'} ${s.time_from?.slice(0, 5)}-${s.time_to?.slice(0, 5)}`).join(', ')}
                          </span>
                        </>
                      )}
                    </div>
                  </div>

                  {/* Radio indicator */}
                  <div className={`w-4 h-4 rounded-full border-2 flex-shrink-0 flex items-center justify-center ${
                    isSelected ? 'border-indigo-500' : 'border-gray-300'
                  }`}>
                    {isSelected && <div className="w-2 h-2 rounded-full bg-indigo-500" />}
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Error */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg px-3 py-2 mb-2 text-xs text-red-700">
            {error}
          </div>
        )}

        {/* Route naar andere dag verplaatsen */}
        {onReschedule && (
          <button
            onClick={onReschedule}
            disabled={isAssigning}
            className="w-full mb-2 py-2 text-sm font-medium text-indigo-600 border border-indigo-200 bg-indigo-50 hover:bg-indigo-100 rounded-lg transition-colors disabled:opacity-50"
          >
            Verplaats naar andere dag…
          </button>
        )}

        {/* Actions */}
        <div className="flex items-center gap-2 pt-2 border-t border-gray-100">
          {isEditMode ? (
            <>
              <button
                onClick={handleAssign}
                disabled={isAssigning || !selectedStaffId || selectedStaffId === currentStaffId || route.locked}
                className="flex-1 py-2.5 bg-indigo-500 text-white rounded-lg text-sm font-medium hover:bg-indigo-600 disabled:opacity-50 transition-colors"
              >
                {isAssigning ? 'Bezig...' : 'Hertoewijzen'}
              </button>
              {onUnassign && (
                <button
                  onClick={onUnassign}
                  disabled={isAssigning || route.locked}
                  className="px-4 py-2.5 text-red-600 hover:bg-red-50 rounded-lg text-sm font-medium transition-colors disabled:opacity-50"
                >
                  Ontkoppelen
                </button>
              )}
            </>
          ) : (
            <button
              onClick={handleAssign}
              disabled={isAssigning || !selectedStaffId || route.locked}
              className="flex-1 py-2.5 bg-indigo-500 text-white rounded-lg text-sm font-medium hover:bg-indigo-600 disabled:opacity-50 transition-colors"
            >
              {isAssigning ? 'Bezig...' : 'Toewijzen'}
            </button>
          )}
          <button
            onClick={onClose}
            className="px-4 py-2.5 text-slate-600 hover:bg-slate-100 rounded-lg text-sm transition-colors"
          >
            Annuleren
          </button>
        </div>
      </div>
    </div>
  );
}
